// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {IconAdd, IconCircleMinus, IconWdt, IconGear, IconNewWindow, IconSwap} from '@deere/icons';
import {exportInventoryReport} from 'Services/excel-service';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {PARTS_CATALOG_URL, SHOP_URL} from 'Ui/components/secondary-navbar/utils/dropdown-navbar-helper';
import {INVENTORY} from 'Common/constants/routes';
import {EDIT_PARTS, MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import {redirect} from 'Utils/redirect-utils';
import {withRouter} from 'react-router-dom';
import {isShopDeereUrlEnabled} from 'Src/ui-core/pages/onequip/parts/inventory';

function getNavbarActions(loading, history, isMigrated, callStockDialog, callPullDialog,
    callExportReport, callTransferDialog, canTransfer, country) {
    const navbarActions = [];

    navbarActions.push(
        {
            onClick: callStockDialog,
            title: 'ONLINK_STOCK',
            Icon: <IconAdd/>,
            variant: 'primary',
            disabled: loading,
            requiredMyJdPermissions: EDIT_PARTS
        },
        {
            onClick: callPullDialog,
            title: 'ONLINK_PULL',
            Icon: <IconCircleMinus/>,
            disabled: loading,
            requiredMyJdPermissions: EDIT_PARTS
        },
        {
            onClick: callExportReport,
            title: 'EXPORT',
            Icon: <IconWdt/>,
            disabled: loading,
            requiredMyJdPermissions: EDIT_PARTS
        },
        {
            hidden: !canTransfer,
            disabled: loading,
            onClick: callTransferDialog,
            title: 'TRANSFER',
            Icon: <IconSwap/>,
            requiredMyJdPermissions: EDIT_PARTS
        },
        {
            onClick: () => window.open(isShopDeereUrlEnabled({
                isMigrated,
                country
            }) ? SHOP_URL : PARTS_CATALOG_URL, '_blank'),
            title: 'SHOP_FOR_PARTS',
            Icon: <IconNewWindow/>
        }
    );

    if (isMigrated) {
        navbarActions.push({
            onClick: () => redirect(`${INVENTORY}/settings/fleet`, history),
            title: 'SETTINGS',
            Icon: <IconGear/>,
            requiredMyJdPermissions: MANAGE_EQUIPMENT_SETTINGS
        });
    }

    return navbarActions;
}

function FilterComponent(props) {
    const {
        addToast,
        dialogOnClose,
        loading,
        membershipHierarchy,
        openTransferInventoryDialog,
        openPullInventoryDialog,
        openStockInventoryDialog,
        partsData,
        translations,
        currencySymbol,
        history,
        includeUnitCostColumn,
        isMigrated,
        country
    } = props;

    const canTransfer = membershipHierarchy.children?.length > 0;

    async function callExportReport() {
        await exportInventoryReport(
            partsData.formattedPartsFromInventoryBins,
            translations,
            currencySymbol,
            addToast,
            includeUnitCostColumn
        );
    }

    function callTransferDialog() {
        openTransferInventoryDialog({
            availableParts: partsData.formattedPartsFromInventoryBins,
            inventories: partsData.inventories,
            membershipHierarchy,
            onClose: dialogOnClose
        });
    }

    function callPullDialog() {
        openPullInventoryDialog({
            availableParts: partsData.formattedPartsFromInventoryBins,
            inventories: partsData.inventories,
            onClose: dialogOnClose
        });
    }

    function callStockDialog() {
        openStockInventoryDialog({
            availableParts: partsData.parts,
            inventories: partsData.inventories,
            onClose: dialogOnClose
        });
    }

    const navBarActions = getNavbarActions(loading, history, isMigrated, callStockDialog, callPullDialog,
        callExportReport, callTransferDialog, canTransfer, country);

    useNavBarActions(navBarActions);
}

FilterComponent.propTypes = {
    addToast: PropTypes.func,
    country: PropTypes.string,
    currencySymbol: PropTypes.string,
    dialogOnClose: PropTypes.func,
    history: PropTypes.history,
    includeUnitCostColumn: PropTypes.bool,
    isMigrated: PropTypes.bool,
    loading: PropTypes.bool,
    membershipHierarchy: PropTypes.object,
    openPullInventoryDialog: PropTypes.func,
    openStockInventoryDialog: PropTypes.func,
    openTransferInventoryDialog: PropTypes.func,
    partsData: PropTypes.object,
    translations: PropTypes.translations
};

export default MediaQuery(MOBILE_MEDIA_QUERY)(withRouter(FilterComponent));
